import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  CheckCircle,
  Shield,
  Star,
  ArrowRight,
  Calendar,
  Search,
  Eye,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Camera,
  Thermometer,
  Home,
  Settings
} from 'lucide-react';
import { SchemaInjector } from '../components/SchemaInjector';

const DiagnosticServicesPricing = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  // Real Diagnostic Service Projects
  const diagnosticProjects = [
    {
      id: 1,
      image: '/colorado-plumbing-uploads/locations/denver/thermal-camera-leak-detection-denver.JPEG',
      location: 'Denver, CO',
      type: 'Difficult Diagnosis - Thermal Imaging',
      description: 'Professional thermal imaging to locate hidden water leaks behind walls',
      features: ['Non-Invasive Detection', 'Precise Location', 'Professional Equipment']
    },
    {
      id: 2,
      image: '/colorado-plumbing-uploads/locations/arvada/thermal-leak-detection-arvada.JPEG',
      location: 'Arvada, CO',
      type: 'Difficult Diagnosis - Thermal Detection',
      description: 'Advanced thermal imaging technology for difficult-to-find leak detection',
      features: ['Thermal Imaging', 'Hidden Leak Detection', 'Advanced Technology']
    },
    {
      id: 3,
      image: '/colorado-plumbing-uploads/locations/arvada/camera-scope-sewer-arvada.JPEG',
      location: 'Arvada, CO',
      type: 'Sewer Camera Inspection',
      description: 'Professional sewer camera inspection to diagnose underground issues',
      features: ['Camera Inspection', 'Sewer Diagnosis', 'Professional Analysis']
    },
    {
      id: 4,
      image: '/colorado-plumbing-uploads/locations/highlands-ranch/sewer-camera-locate-highlands-ranch.JPEG',
      location: 'Highlands Ranch, CO',
      type: 'Sewer Camera Locate Service',
      description: 'Precision sewer camera location service for accurate pipe mapping',
      features: ['Pipe Location', 'Precision Mapping', 'Professional Service']
    },
    {
      id: 5,
      image: '/colorado-plumbing-uploads/locations/centennial/water-leak-ceiling-centennial.JPEG',
      location: 'Centennial, CO',
      type: 'Water Leak Detection',
      description: 'Professional water leak detection and assessment service',
      features: ['Leak Detection', 'Damage Assessment', 'Professional Inspection']
    },
    {
      id: 6,
      image: '/colorado-plumbing-uploads/locations/thornton/locate-pipe-with-sewer-camera-thornton.JPEG',
      location: 'Thornton, CO',
      type: 'Pipe Location with Camera',
      description: 'Professional pipe location service using advanced camera technology',
      features: ['Pipe Location', 'Camera Technology', 'Precise Mapping']
    }
  ];

  // Unified Schema for the entire page
  const completePageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://colorado-plumbing.com/diagnostic-services-pricing#webpage",
        "url": "https://colorado-plumbing.com/diagnostic-services-pricing",
        "name": "Denver Plumbing Diagnostic Cost | Leak Detection $225 | Colorado First Plumbing",
        "description": "See exact, flat-rate prices for plumbing diagnostics in Denver. Standard Diagnosis: $95. Difficult Leak Detection: $225. Sewer Camera: $175. 360 Google Reviews.",
        "isPartOf": { "@id": "https://colorado-plumbing.com/#website" },
        "breadcrumb": { "@id": "https://colorado-plumbing.com/diagnostic-services-pricing#breadcrumb" }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://colorado-plumbing.com/diagnostic-services-pricing#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://colorado-plumbing.com/" },
          { "@type": "ListItem", "position": 2, "name": "Diagnostic Services Pricing", "item": "https://colorado-plumbing.com/diagnostic-services-pricing" }
        ]
      },
      {
        "@type": "Plumber",
        "@id": "https://colorado-plumbing.com/",
        "name": "Colorado First Plumbing and Sewer LLC",
        "image": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Colorado-First-Plumbing-Logo-Transparent.png",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "5108 Sydney Ave", "addressLocality": "Littleton", "addressRegion": "CO", "postalCode": "80130", "addressCountry": "US"
        },
        "url": "https://colorado-plumbing.com/diagnostic-services-pricing",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" },
        "areaServed": [
          { "@type": "City", "name": "Denver" },
          { "@type": "AdministrativeArea", "name": "Denver Metro" }
        ]
      },
      {
        "@type": "Product", "name": "Standard Plumbing Diagnosis Service",
        "description": "Our $95 flat-rate Standard Diagnosis includes a visual inspection and analysis by a licensed plumber to identify repair options for straightforward plumbing issues.",
        "sku": "DIAG-STD-95",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/diagnostic-services-pricing#diagnostic-services-pricing-details", "priceCurrency": "USD", "price": "95", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Difficult Plumbing Diagnosis Service (Leak Detection)",
        "description": "Our $225 flat-rate Difficult Diagnosis is for complex issues like hidden leaks. It includes the use of advanced equipment like thermal imaging cameras and gas sniffers to precisely locate the problem.",
        "sku": "DIAG-DIFF-225",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/diagnostic-services-pricing#diagnostic-services-pricing-details", "priceCurrency": "USD", "price": "225", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Sewer Camera Inspection Service",
        "description": "A $175 flat-rate professional video inspection of your main sewer line from an accessible cleanout. Includes a digital copy of the footage and a detailed report. Fee is deducted from any resulting sewer repair.",
        "sku": "DIAG-CAM-175",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/diagnostic-services-pricing#diagnostic-services-pricing-details", "priceCurrency": "USD", "price": "175", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Annual Plumbing Checkup Service",
        "description": "A comprehensive $350 preventative maintenance inspection of your entire home's plumbing system. Includes fixture checks, water heater assessment, pressure evaluation, and a sewer camera inspection (if accessible).",
        "sku": "DIAG-ANNUAL-350",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/diagnostic-services-pricing#diagnostic-services-pricing-details", "priceCurrency": "USD", "price": "350", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "Product", "name": "Home Valve Labeling Service",
        "description": "For $275, our plumbers will professionally identify and apply durable labels to up to 10 of your home's main water and gas shut-off valves for emergency preparedness.",
        "sku": "DIAG-LABEL-275",
        "offers": { "@type": "Offer", "url": "https://colorado-plumbing.com/diagnostic-services-pricing#diagnostic-services-pricing-details", "priceCurrency": "USD", "price": "275", "availability": "https://schema.org/InStock" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "360" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question", "name": "What is the difference between a Standard and a Difficult Diagnosis?",
            "acceptedAnswer": { "@type": "Answer", "text": "Our Standard Diagnosis ($95) is a visual inspection for straightforward issues like a visible leak or malfunctioning fixture. A Difficult Diagnosis ($225) is for complex problems that require specialized equipment, such as thermal imaging to find a leak behind a wall or a gas sniffer to locate a gas leak." }
          },
          {
            "@type": "Question", "name": "How much does a sewer camera inspection cost in Denver?",
            "acceptedAnswer": { "@type": "Answer", "text": "A professional sewer camera inspection in the Denver area costs a flat-rate of $175. This includes a full video inspection of your main sewer line from an accessible cleanout and a digital copy of the footage. This fee is waived or deducted if you proceed with a recommended sewer repair." }
          },
          {
            "@type": "Question", "name": "What is included in the Annual Plumbing Checkup?",
            "acceptedAnswer": { "@type": "Answer", "text": "Our $350 Annual Plumbing Checkup is a comprehensive home inspection. It includes checking all plumbing fixtures, assessing the water heater, evaluating water pressure, and performing a sewer camera inspection if a suitable cleanout is accessible. You receive a full report with preventative care recommendations." }
          },
          {
            "@type": "Question", "name": "What is the Home Valve Labeling Service?",
            "acceptedAnswer": { "@type": "Answer", "text": "For $275, our plumbers will identify and label up to 10 of your home's crucial water and gas shut-off valves. This service is designed for emergency preparedness, so you can quickly shut off the correct valve in a crisis, preventing potential water damage." }
          }
        ]
      }
    ]
  };

  // SEO meta tags setup
  useEffect(() => {
    // Auto-advance hero gallery every 5 seconds
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % diagnosticProjects.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [diagnosticProjects.length]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % diagnosticProjects.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + diagnosticProjects.length) % diagnosticProjects.length);
  };

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-950">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-blue-800 via-blue-700 to-blue-600 overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          
          <div className="container mx-auto px-4 text-center relative z-10 py-16 md:py-24">
            <div className="inline-block px-6 py-3 mb-8 rounded-full bg-action-warm/90 text-white font-bold text-lg border-2 border-action-primary/30 backdrop-blur-sm">
              🔍 Standard & Advanced Detection • Professional Analysis • Problem Prevention
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-neutral-white leading-tight">
              Denver Plumbing Diagnostic Costs
              <span className="block text-2xl md:text-3xl text-blue-200 font-semibold mt-2">
                Exact Prices for Leak Detection & Camera Inspections
              </span>
            </h1>
            
            <div className="max-w-4xl mx-auto mb-8">
              <p className="text-lg text-blue-100">
                Master Plumber Owned • 360 Google Reviews • Thermal Imaging Technology • Sewer Camera Inspection
              </p>
            </div>

            {/* Primary CTA - Action Color Psychology */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <a 
                href="#diagnostic-services-pricing-details" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 See All Pricing Below
              </a>
            </div>
          </div>
        </section>

        {/* Trust & Authority Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Denver Metro Trusts Our Diagnostic Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Littleton, Denver Metro, and surrounding areas</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">360</div>
                <div className="text-lg font-semibold text-white mb-2">Google Reviews</div>
                <p className="text-blue-100 text-sm">4.8★ rating with verified customer feedback</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities and counties</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited business with excellent track record</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">Showing Exact Diagnostic Prices Online with 360 Google Reviews</p>
              </div>
            </div>
          </div>
        </section>

        {/* Proof of Work Gallery Section */}
        <section className="py-16 bg-gradient-to-br from-neutral-white to-trust-light/30">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-success-primary text-white font-bold text-lg">
                🏆 Real Projects • Advanced Equipment • Professional Results
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 text-authority-navy">
                See Our Professional Diagnostic Services
              </h2>
              <p className="text-xl text-neutral-gray mb-4">
                Colorado First Plumbing diagnostic services across Denver Metro • Advanced technology • Expert analysis
              </p>
            </div>

            {/* Hero Gallery Carousel */}
            <div className="relative mb-12 max-w-5xl mx-auto">
              <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border-4 border-trust-primary/20">
                <img
                  src={diagnosticProjects[currentImageIndex].image}
                  alt={`${diagnosticProjects[currentImageIndex].type} in ${diagnosticProjects[currentImageIndex].location} - Colorado First Plumbing`}
                  className="w-full h-full object-cover transition-all duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/80 via-transparent to-transparent"></div>
                
                {/* Project Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between">
                    <div className="mb-4 md:mb-0">
                      <div className="flex items-center mb-2">
                        <MapPin size={20} className="text-success-eco mr-2" />
                        <span className="text-success-light font-semibold">{diagnosticProjects[currentImageIndex].location}</span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold mb-2">
                        {diagnosticProjects[currentImageIndex].type}
                      </h3>
                      <p className="text-lg text-trust-light mb-3">
                        {diagnosticProjects[currentImageIndex].description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {diagnosticProjects[currentImageIndex].features.map((feature, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-success-primary/90 rounded-full text-sm font-medium"
                          >
                            ✓ {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-success-eco mb-1">
                        {currentImageIndex + 1} / {diagnosticProjects.length}
                      </div>
                      <div className="text-sm text-trust-light">Professional Projects</div>
                    </div>
                  </div>
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-authority-navy p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={24} className="group-hover:scale-110 transition-transform" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-authority-navy p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 group"
                  aria-label="Next image"
                >
                  <ChevronRight size={24} className="group-hover:scale-110 transition-transform" />
                </button>
              </div>

              {/* Dots Navigation */}
              <div className="flex justify-center mt-6 space-x-2">
                {diagnosticProjects.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-200 ${
                      index === currentImageIndex
                        ? 'bg-success-primary scale-125'
                        : 'bg-neutral-border hover:bg-trust-primary'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Project Grid Gallery */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
              {diagnosticProjects.map((project, index) => (
                <div
                  key={project.id}
                  className={`group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-2 ${
                    index === currentImageIndex 
                      ? 'border-success-primary ring-4 ring-success-primary/30 scale-105' 
                      : 'border-neutral-border hover:border-trust-primary'
                  }`}
                  onClick={() => {
                    setCurrentImageIndex(index);
                    openLightbox(index);
                  }}
                >
                  <div className="aspect-square relative overflow-hidden">
                    <img
                      src={project.image}
                      alt={`${project.type} in ${project.location} - Colorado First Plumbing`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <div className="flex items-center mb-1">
                        <MapPin size={14} className="text-success-eco mr-1" />
                        <span className="text-xs font-semibold">{project.location}</span>
                      </div>
                      <h4 className="text-sm font-bold leading-tight">{project.type}</h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Call to Action */}
            <div className="text-center">
              <div className="bg-gradient-to-r from-success-primary to-success-eco rounded-2xl p-8 shadow-xl border border-success-primary/20 max-w-4xl mx-auto">
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                  Ready for Professional Diagnostic Service?
                </h3>
                <p className="text-lg mb-6 text-success-light">
                  See your exact diagnostic service cost below • Advanced equipment • Professional analysis guaranteed
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  
                  <a
                    href="#diagnostic-services-pricing-details"
                    className="bg-gradient-to-r from-trust-primary to-trust-deep hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-lg font-semibold inline-block transition-all duration-300"
                  >
                    💰 See Pricing Below
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Image Lightbox Modal */}
        {selectedImage !== null && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={closeLightbox}>
            <div className="relative max-w-6xl w-full max-h-full">
              <img
                src={diagnosticProjects[selectedImage].image}
                alt={`${diagnosticProjects[selectedImage].type} in ${diagnosticProjects[selectedImage].location} - Colorado First Plumbing`}
                className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white text-authority-navy p-2 rounded-full shadow-lg transition-all duration-200"
                aria-label="Close lightbox"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="absolute bottom-4 left-4 right-4 bg-black/80 rounded-lg p-4 text-white">
                <div className="flex items-center mb-2">
                  <MapPin size={20} className="text-success-eco mr-2" />
                  <span className="text-success-light font-semibold">{diagnosticProjects[selectedImage].location}</span>
                </div>
                <h3 className="text-xl font-bold mb-2">{diagnosticProjects[selectedImage].type}</h3>
                <p className="text-trust-light mb-3">{diagnosticProjects[selectedImage].description}</p>
                <div className="flex flex-wrap gap-2">
                  {diagnosticProjects[selectedImage].features.map((feature, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-success-primary/90 rounded-full text-sm font-medium"
                    >
                      ✓ {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Pricing Section */}
        <section id="diagnostic-services-pricing-details" className="py-20 bg-neutral-white">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-authority-navy">
                Transparent Diagnostic Services Pricing
              </h2>
              <p className="text-xl text-neutral-gray mb-4">
                See actual diagnostic service costs with no hidden fees or surprise charges
              </p>
              <div className="inline-flex items-center bg-success-light px-6 py-3 rounded-full border-2 border-success-primary/20">
                <Shield className="w-6 h-6 text-success-primary mr-3" />
                <span className="text-success-primary font-bold">Professional Equipment • Expert Analysis • All Pricing Online</span>
              </div>
            </div>

            {/* Standard Diagnosis Fee Service */}
            <div className="mb-16">
              <div className="bg-gradient-to-r from-trust-light to-success-light/30 rounded-2xl p-8 border-2 border-trust-primary/20 mb-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mr-6 shadow-lg">
                    <Search size={32} className="text-neutral-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-authority-navy mb-2">Standard Diagnosis Fee</h3>
                    <p className="text-lg text-neutral-gray">Visual inspection and analysis • Identify repair options • Basic assessment</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Pricing Table */}
                  <div>
                    <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                      <div className="bg-gradient-to-r from-trust-primary to-trust-deep text-white p-4">
                        <h4 className="text-xl font-bold text-center">Standard Diagnosis Pricing</h4>
                      </div>
                      <div className="p-6 space-y-4">
                        <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                          <span className="font-semibold text-authority-navy">Standard Diagnosis Service</span>
                          <span className="text-3xl font-bold text-success-primary">$95</span>
                        </div>
                      </div>
                      <div className="bg-success-light p-4">
                        <h5 className="font-bold text-success-primary mb-3">Standard Diagnosis Includes</h5>
                        <ul className="space-y-2 text-sm text-success-primary">
                          <li className="flex items-center">
                            <CheckCircle size={16} className="mr-2" />
                            Visual inspection and analysis
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="mr-2" />
                            Identify repair and replacement options
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="mr-2" />
                            Basic assessment report
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="mr-2" />
                            Expert recommendations
                          </li>
                        </ul>
                      </div>
                      
                      <div className="bg-blue-50 p-4 border-t border-blue-200">
                        <p className="text-blue-800 font-semibold text-sm mb-2">*Perfect For:</p>
                        <ul className="text-blue-700 text-xs space-y-1">
                          <li>• Water heater assessment</li>
                          <li>• Sump pump inspection</li>
                          <li>• Water pressure evaluation</li>
                          <li>• General plumbing concerns</li>
                          <li>• Fixture troubleshooting</li>
                          <li>• Does NOT include specialized equipment</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Photo Gallery */}
                  <div>
                    <div className="bg-neutral-white rounded-xl shadow-lg border border-trust-primary/10 overflow-hidden">
                      <div className="aspect-video relative">
                        <img
                          src="/colorado-plumbing-uploads/locations/arvada/camera-scope-sewer-arvada.JPEG"
                          alt="Standard diagnosis service visual inspection Colorado"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <h4 className="text-lg font-bold mb-1">
                            Standard Diagnosis Service - Visual Inspection
                          </h4>
                          <p className="text-sm text-trust-light">
                            Basic assessment and analysis using standard tools
                          </p>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-bold text-authority-navy mb-2">Standard Diagnosis Process</h4>
                        <ul className="space-y-2 text-sm text-neutral-gray">
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Thorough visual inspection
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            System functionality testing
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Basic problem identification
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Repair recommendations
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Difficult Diagnosis Service */}
            <div className="mb-16">
              <div className="bg-gradient-to-r from-action-warm/20 to-action-primary/20 rounded-2xl p-8 border-2 border-action-primary/20 mb-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mr-6 shadow-lg">
                    <Thermometer size={32} className="text-neutral-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-authority-navy mb-2">Difficult Diagnosis Service</h3>
                    <p className="text-lg text-neutral-gray">Advanced equipment • Hard-to-find leaks • Thermal imaging • Gas sniffers</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Difficult Diagnosis Pricing */}
                  <div>
                    <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                      <div className="bg-gradient-to-r from-action-primary to-action-urgent text-white p-4">
                        <h4 className="text-xl font-bold text-center">Difficult Diagnosis Pricing</h4>
                      </div>
                      <div className="p-6">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                            <span className="font-semibold text-authority-navy">Difficult Diagnosis Service</span>
                            <span className="text-3xl font-bold text-success-primary">$225</span>
                          </div>
                        </div>
                        <div className="bg-action-warm/10 rounded-lg p-4 mt-6">
                          <h5 className="font-bold text-authority-navy mb-3">Difficult Diagnosis Includes</h5>
                          <ul className="space-y-2 text-sm text-neutral-gray">
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Thermal imaging camera detection
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Gas leak detection equipment
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Advanced leak detection tools
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Hidden leak location services
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Comprehensive analysis report
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Specialized equipment operation
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="bg-orange-50 p-4 border-t border-orange-200">
                        <p className="text-orange-800 font-semibold text-sm mb-2">*Perfect For:</p>
                        <ul className="text-orange-700 text-xs space-y-1">
                          <li>• Hard-to-find water leaks behind walls</li>
                          <li>• Gas leak detection and location</li>
                          <li>• Thermal imaging leak detection</li>
                          <li>• Hidden plumbing issues</li>
                          <li>• Advanced diagnostic requirements</li>
                          <li>• When standard diagnosis is insufficient</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Difficult Diagnosis Photo */}
                  <div>
                    <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                      <div className="aspect-video relative">
                        <img
                          src="/colorado-plumbing-uploads/locations/denver/thermal-camera-leak-detection-denver.JPEG"
                          alt="Thermal camera leak detection difficult diagnosis Denver Colorado"
                          className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <h4 className="text-lg font-bold mb-1">
                            Thermal Camera Leak Detection - Denver
                          </h4>
                          <p className="text-sm text-trust-light">
                            Advanced thermal imaging to locate hidden leaks behind walls
                          </p>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-bold text-authority-navy mb-2">Advanced Diagnostic Equipment</h4>
                        <ul className="space-y-2 text-sm text-neutral-gray">
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Professional thermal imaging camera
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Gas detection and sniffing equipment
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Non-invasive leak detection
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Precise leak location mapping
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sewer Camera Inspection */}
            <div className="mb-16">
              <div className="bg-gradient-to-r from-blue-100 to-blue-200/50 rounded-2xl p-8 border-2 border-blue-300/30 mb-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center mr-6 shadow-lg">
                    <Camera size={32} className="text-neutral-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-authority-navy mb-2">Sewer Camera Inspection</h3>
                    <p className="text-lg text-neutral-gray">Advanced camera technology • Complete sewer line inspection • Professional footage report</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Sewer Camera Pricing */}
                  <div>
                    <div className="bg-neutral-white rounded-xl shadow-lg border border-blue-300/20 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4">
                        <h4 className="text-xl font-bold text-center">Sewer Camera Inspection Pricing</h4>
                      </div>
                      <div className="p-6">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                            <span className="font-semibold text-authority-navy">Sewer Camera Inspection</span>
                            <span className="text-3xl font-bold text-success-primary">$175</span>
                          </div>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 mt-6">
                          <h5 className="font-bold text-authority-navy mb-3">Camera Inspection Includes</h5>
                          <ul className="space-y-2 text-sm text-neutral-gray">
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Advanced camera inspection equipment
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Complete main sewer line inspection
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Professional video footage recording
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Detailed inspection report emailed
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Problem identification and recommendations
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Standard cleanout access included
                            </li>
                          </ul>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-4 mt-4">
                          <p className="text-yellow-800 font-semibold text-sm mb-2">Important Service Notes:</p>
                          <p className="text-yellow-700 text-sm mb-2">
                            Get a clear view of your plumbing with our Sewer Camera Inspection service. We use advanced cameras to thoroughly inspect your main sewer line, starting with standard access through an accessible cleanout.
                          </p>
                          <p className="text-yellow-700 text-sm mb-2">
                            Additional fees apply for toilet removal, roof access, or other complex entry points. Prices listed are starting rates and may vary based on accessibility or findings.
                          </p>
                          <p className="text-yellow-700 text-sm font-medium">
                            A detailed report and footage will be emailed to you for your records—contact us to schedule!
                          </p>
                        </div>
                      </div>
                      <div className="bg-blue-50 p-4 border-t border-blue-200">
                        <p className="text-blue-800 font-semibold text-sm mb-2">*Perfect For:</p>
                        <ul className="text-blue-700 text-xs space-y-1">
                          <li>• Slow drain diagnosis and investigation</li>
                          <li>• Pre-purchase home inspection</li>
                          <li>• Recurring sewer backup issues</li>
                          <li>• Property maintenance assessment</li>
                          <li>• Root intrusion detection</li>
                          <li>• Pipe condition evaluation</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Sewer Camera Photo */}
                  <div>
                    <div className="bg-neutral-white rounded-xl shadow-lg border border-blue-300/20 overflow-hidden">
                      <div className="aspect-video relative">
                        <img
                          src="/colorado-plumbing-uploads/locations/highlands-ranch/sewer-camera-locate-highlands-ranch.JPEG"
                          alt="Sewer camera inspection equipment Colorado First Plumbing"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <h4 className="text-lg font-bold mb-1">
                            Professional Sewer Camera Inspection - Highlands Ranch
                          </h4>
                          <p className="text-sm text-trust-light">
                            Advanced camera technology provides clear view of sewer line condition
                          </p>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-bold text-authority-navy mb-2">Advanced Camera Technology</h4>
                        <ul className="space-y-2 text-sm text-neutral-gray">
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            High-definition video recording
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Real-time problem identification
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Professional grade equipment
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Complete footage documentation
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Annual Plumbing Check Up */}
            <div className="mb-16">
              <div className="bg-gradient-to-r from-action-warm/20 to-action-primary/20 rounded-2xl p-8 border-2 border-action-primary/20 mb-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mr-6 shadow-lg">
                    <Home size={32} className="text-neutral-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-authority-navy mb-2">Annual Plumbing Check Up</h3>
                    <p className="text-lg text-neutral-gray">Complete home plumbing inspection • Preventative care • Comprehensive assessment</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Annual Checkup Pricing */}
                  <div>
                    <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                      <div className="bg-gradient-to-r from-action-primary to-action-urgent text-white p-4">
                        <h4 className="text-xl font-bold text-center">Annual Plumbing Check Up Pricing</h4>
                      </div>
                      <div className="p-6">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                            <span className="font-semibold text-authority-navy">Complete Home Plumbing Inspection</span>
                            <span className="text-3xl font-bold text-success-primary">$350</span>
                          </div>
                        </div>
                        <div className="bg-action-warm/10 rounded-lg p-4 mt-6">
                          <h5 className="font-bold text-authority-navy mb-3">Annual Check Up Includes</h5>
                          <ul className="space-y-2 text-sm text-neutral-gray">
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              All plumbing fixtures inspection
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Water heater assessment
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Pipe and connection review
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Preventative care recommendations
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Mainline camera (if cleanout accessible)
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Comprehensive service report
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="bg-green-50 p-4 border-t border-green-200">
                        <p className="text-green-800 font-semibold text-sm mb-2">*Preventative Maintenance Benefits:</p>
                        <ul className="text-green-700 text-xs space-y-1">
                          <li>• Catch issues before they become expensive</li>
                          <li>• Extend equipment lifespan</li>
                          <li>• Maintain home water quality</li>
                          <li>• Peace of mind with professional assessment</li>
                          <li>• Annual service documentation</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Annual Checkup Photo */}
                  <div>
                    <div className="bg-neutral-white rounded-xl shadow-lg border border-action-primary/10 overflow-hidden">
                      <div className="aspect-video relative">
                        <img
                          src="/colorado-plumbing-uploads/locations/arvada/camera-scope-sewer-arvada.JPEG"
                          alt="Annual plumbing checkup sewer camera inspection"
                          className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <h4 className="text-lg font-bold mb-1">
                            Annual Checkup - Sewer Camera Inspection
                          </h4>
                          <p className="text-sm text-trust-light">
                            Comprehensive inspection including mainline camera when accessible
                          </p>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-bold text-authority-navy mb-2">Comprehensive Home Assessment</h4>
                        <ul className="space-y-2 text-sm text-neutral-gray">
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Fixture operation testing
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Water pressure evaluation
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Leak detection assessment
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Preventative maintenance plan
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Home Valve Labeling Service */}
            <div className="mb-16">
              <div className="bg-gradient-to-r from-purple-100 to-purple-200/50 rounded-2xl p-8 border-2 border-purple-300/30 mb-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-purple-800 rounded-full flex items-center justify-center mr-6 shadow-lg">
                    <Settings size={32} className="text-neutral-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-authority-navy mb-2">Home Valve Labeling Service</h3>
                    <p className="text-lg text-neutral-gray">Professional valve identification • Clear labeling • Emergency preparedness</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Valve Labeling Pricing */}
                  <div>
                    <div className="bg-neutral-white rounded-xl shadow-lg border border-purple-300/20 overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white p-4">
                        <h4 className="text-xl font-bold text-center">Home Valve Labeling Pricing</h4>
                      </div>
                      <div className="p-6">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center py-3 border-b border-neutral-border">
                            <span className="font-semibold text-authority-navy">Up to 10 Valves (2500 sq ft)</span>
                            <span className="text-3xl font-bold text-success-primary">$275</span>
                          </div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 mt-6">
                          <h5 className="font-bold text-authority-navy mb-3">Valve Labeling Includes</h5>
                          <ul className="space-y-2 text-sm text-neutral-gray">
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Main water shut-off identification
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Gas shut-off valve labeling
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Sprinkler system valve labels
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Irrigation valve identification
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Fixture isolation valve labels
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={16} className="text-success-primary mr-2" />
                              Professional labeling materials
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="bg-yellow-50 p-4 border-t border-yellow-200">
                        <p className="text-yellow-800 font-semibold text-sm mb-2">*Service Specifications:</p>
                        <ul className="text-yellow-700 text-xs space-y-1">
                          <li>• Standard service covers up to 2500 sq ft homes</li>
                          <li>• Includes labeling of up to 10 valves</li>
                          <li>• Additional valves available for extra charge</li>
                          <li>• Weather-resistant labeling materials</li>
                          <li>• Clear identification for emergency situations</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Valve Labeling Photo */}
                  <div>
                    <div className="bg-neutral-white rounded-xl shadow-lg border border-purple-300/20 overflow-hidden">
                      <div className="aspect-video relative">
                        <img
                          src="/colorado-plumbing-uploads/locations/centennial/water-leak-ceiling-centennial.JPEG"
                          alt="Professional valve labeling emergency preparedness service"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-authority-navy/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <h4 className="text-lg font-bold mb-1">
                            Emergency Preparedness - Valve Labeling
                          </h4>
                          <p className="text-sm text-trust-light">
                            Know exactly which valve controls what in emergency situations
                          </p>
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-bold text-authority-navy mb-2">Professional Valve Identification</h4>
                        <ul className="space-y-2 text-sm text-neutral-gray">
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Clear, permanent labeling
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Emergency shut-off preparation
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Weather-resistant materials
                          </li>
                          <li className="flex items-center">
                            <CheckCircle size={16} className="text-success-primary mr-2" />
                            Professional installation
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section for Schema Parity */}
            <section className="py-16 bg-gradient-to-br from-neutral-white to-trust-light/30">
              <div className="container mx-auto px-4 max-w-4xl">
                <h2 className="text-3xl md:text-5xl font-bold mb-12 text-authority-navy text-center">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  <div className="bg-white p-6 rounded-lg shadow-md border">
                    <h3 className="font-bold text-lg text-authority-navy">What is the difference between a Standard and a Difficult Diagnosis?</h3>
                    <p className="text-neutral-gray mt-2">Our Standard Diagnosis ($95) is a visual inspection for straightforward issues like a visible leak or malfunctioning fixture. A Difficult Diagnosis ($225) is for complex problems that require specialized equipment, such as thermal imaging to find a leak behind a wall or a gas sniffer to locate a gas leak.</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md border">
                    <h3 className="font-bold text-lg text-authority-navy">How much does a sewer camera inspection cost in Denver?</h3>
                    <p className="text-neutral-gray mt-2">A professional sewer camera inspection in the Denver area costs a flat-rate of $175. This includes a full video inspection of your main sewer line from an accessible cleanout and a digital copy of the footage. This fee is waived or deducted if you proceed with a recommended sewer repair.</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md border">
                    <h3 className="font-bold text-lg text-authority-navy">What is included in the Annual Plumbing Checkup?</h3>
                    <p className="text-neutral-gray mt-2">Our $350 Annual Plumbing Checkup is a comprehensive home inspection. It includes checking all plumbing fixtures, assessing the water heater, evaluating water pressure, and performing a sewer camera inspection if a suitable cleanout is accessible. You receive a full report with preventative care recommendations.</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md border">
                    <h3 className="font-bold text-lg text-authority-navy">What is the Home Valve Labeling Service?</h3>
                    <p className="text-neutral-gray mt-2">For $275, our plumbers will identify and label up to 10 of your home's crucial water and gas shut-off valves. This service is designed for emergency preparedness, so you can quickly shut off the correct valve in a crisis, preventing potential water damage.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <div className="text-center">
              <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 text-white shadow-2xl border border-trust-primary/20">
                <h3 className="text-3xl font-bold mb-4">Ready for Professional Diagnostic Services?</h3>
                <p className="text-xl mb-6 text-trust-light">
                  See your exact price above • Advanced equipment • Professional analysis • Master plumber owned
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  
                  <a 
                    href="/pricing" 
                    className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full sm:w-auto"
                  >
                    🔧 See More Services
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      
      {/* Schema Injection */}
      <SchemaInjector 
        schemas={[completePageSchema]}
        title="Denver Plumbing Diagnostic Cost | Leak Detection $225 | Colorado First"
        description="See exact, flat-rate prices for plumbing diagnostics in Denver. Standard Diagnosis: $95. Difficult Leak Detection: $225. Sewer Camera: $175. 360 Google Reviews."
        canonical="https://colorado-plumbing.com/diagnostic-services-pricing"
        keywords="denver plumbing diagnostic cost, leak detection price, sewer camera inspection cost denver, thermal imaging plumbing, annual plumbing checkup"
      />
    </div>
  );
};

export default DiagnosticServicesPricing;